import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageSquare, 
  Phone, 
  Clock, 
  Send,
  User,
  Calendar,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppConversationsPage = () => {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/whatsapp/conversations/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        phone_number: selectedConversation.phone_number,
        message: replyText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReplyText('');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      alert('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const getStepLabel = (step) => {
    const labels = {
      greeting: 'Inicio',
      name: 'Nombre',
      phone: 'Teléfono',
      date: 'Fecha',
      time: 'Hora',
      reason: 'Motivo',
      confirmation: 'Confirmación',
      completed: 'Completado'
    };
    return labels[step] || step;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-conversations-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Conversaciones WhatsApp</h1>
        <p className="text-slate-500 mt-1">Historial de conversaciones con pacientes</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Conversations List */}
        <Card className={`border-0 shadow-sm ${selectedConversation ? 'hidden lg:block' : ''}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-500" />
              Conversaciones ({conversations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {conversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No hay conversaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-slate-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {conv.captured_name || conv.phone_number}
                            </p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {conv.phone_number}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                      
                      {conv.last_message && (
                        <p className="mt-2 text-sm text-slate-600 truncate">
                          {conv.last_message.message}
                        </p>
                      )}
                      
                      <div className="mt-2 flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {getStepLabel(conv.current_step)}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {format(new Date(conv.created_at), 'dd MMM, HH:mm', { locale: es })}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat View */}
        <Card className={`border-0 shadow-sm lg:col-span-2 ${!selectedConversation ? 'hidden lg:flex' : ''} flex flex-col`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <CardHeader className="border-b border-slate-100 pb-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="lg:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">
                      {selectedConversation.captured_name || selectedConversation.phone_number}
                    </p>
                    <p className="text-sm text-slate-500">{selectedConversation.phone_number}</p>
                  </div>
                  {selectedConversation.captured_date && (
                    <Badge className="bg-[#0891B2]/10 text-[#0891B2]">
                      <Calendar className="h-3 w-3 mr-1" />
                      {selectedConversation.captured_date} {selectedConversation.captured_time}
                    </Badge>
                  )}
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[400px] p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'incoming' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                            msg.direction === 'incoming'
                              ? 'bg-slate-100 text-slate-900 rounded-bl-md'
                              : 'bg-[#0891B2] text-white rounded-br-md'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.message}</p>
                          <p className={`text-xs mt-1 ${
                            msg.direction === 'incoming' ? 'text-slate-400' : 'text-white/70'
                          }`}>
                            {format(new Date(msg.created_at), 'HH:mm', { locale: es })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Reply Input */}
              <div className="p-4 border-t border-slate-100">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                    placeholder="Escribe un mensaje..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button
                    onClick={sendReply}
                    disabled={!replyText.trim() || sending}
                    className="bg-[#0891B2] hover:bg-[#0e7490]"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Selecciona una conversación para ver los mensajes</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default WhatsAppConversationsPage;
